import { assertNever } from '~/util';
import { DELTA_T } from '../waveform';
import type { WaveformApi } from '../waveform';
import type { ConstantWaveform } from '~/models/studio';
import type { Bounds, Vec2 } from '~/util';

export const wfConstant: WaveformApi<ConstantWaveform> = {
  samples: constantSamples,
  sampleAt: calcConstantValue,
  bounds: constantBounds,
  points: constantPoints,
  duration: calcConstantDuration,
  area: calcConstantArea
};

function constantSamples(wf: ConstantWaveform): number[] {
  const duration = calcConstantDuration(wf);
  const value = calcConstantValue(wf);
  return Array(duration).fill(value);
}

function constantBounds(wf: ConstantWaveform): Bounds {
  const value = calcConstantValue(wf);
  return { min: value, max: value };
}

function constantPoints(wf: ConstantWaveform, xOffset: number): Vec2[] {
  const duration = calcConstantDuration(wf);
  const value = calcConstantValue(wf);
  return [
    { x: xOffset, y: value },
    { x: xOffset + duration, y: value }
  ];
}

function calcConstantValue(wf: ConstantWaveform): number {
  switch (wf.constraint.kind) {
    case 'area':
    case 'duration':
      return wf.constraint.maxVal;
    case 'maxVal':
      if (wf.constraint.duration === 0) {
        return 0;
      }
      return wf.constraint.area / (wf.constraint.duration * DELTA_T);
    default:
      assertNever(wf.constraint);
  }
}

function calcConstantArea(wf: ConstantWaveform): number {
  switch (wf.constraint.kind) {
    case 'duration':
    case 'maxVal':
      return wf.constraint.area;
    case 'area':
      return wf.constraint.maxVal * (wf.constraint.duration * DELTA_T);
    default:
      assertNever(wf.constraint);
  }
}

function calcConstantDuration(wf: ConstantWaveform): number {
  switch (wf.constraint.kind) {
    case 'maxVal':
    case 'area':
      return wf.constraint.duration;
    case 'duration':
      if (wf.constraint.maxVal === 0) {
        return 0;
      }
      return Math.max(
        0,
        Math.floor(wf.constraint.area / wf.constraint.maxVal / DELTA_T)
      );
    default:
      assertNever(wf.constraint);
  }
}
